import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";

import { TABLE_EMPTY_MESSAGE, actionTooltipProps } from "@/ds";
import { AcoesCelula, DataCelula, NomeCelula, PapelCelula } from "../alocacao-celulas";
import type { EquipeRow } from "../equipe-obra.types";
import celulas from "../alocacao-celulas/alocacao-celulas.module.scss";
import {
  EQUIPE_COLUMN_ACOES,
  EQUIPE_COLUMN_FIM,
  EQUIPE_COLUMN_INICIO,
  EQUIPE_COLUMN_NOME,
  EQUIPE_COLUMN_PAPEL,
} from "../equipe-obra.constants";
import styles from "./equipe-tabela.module.scss";
import type { EquipeTabelaProps, EquipeTabelaViewModel } from "./types-equipe-tabela";

const CLASSE_LINHA: Record<string, string | undefined> = {
  falhou: styles.falhou,
  leitura: styles.leitura,
  "": undefined,
};

/**
 * Tabela de alocações da equipe: Nome, Papel na Obra, Data Início, Data Fim e as ações da linha.
 * A linha em edição troca texto por campo; na equipe gravada só as datas editam.
 */
export function EquipeTabelaView(props: EquipeTabelaProps & EquipeTabelaViewModel) {
  const {
    linhas,
    papeis,
    profissionaisError,
    disabled,
    opcoes,
    conflitosDaLinha,
    nomeDe,
    mudar,
    assinatura,
    confirmar,
    cancelar,
    editar,
    remover,
    testIdLinha,
    classeLinha,
  } = props;

  return (
    <>
      <Tooltip
        key={assinatura}
        target={`.${celulas.truncavel}`}
        {...actionTooltipProps("", { event: "hover" })}
      />
      <DataTable
        value={linhas}
        dataKey="id"
        emptyMessage={TABLE_EMPTY_MESSAGE}
        // O Prime memoriza as células pela linha e ignora o `body`: as opções de profissionais e papéis chegam
        // de fora da linha, e sem isto Nome e Papel ficam em "—" quando elas carregam depois.
        cellMemo={false}
        size="small"
        className={styles.table}
        tableClassName={styles.tabela}
        rowClassName={(linha: EquipeRow) => CLASSE_LINHA[classeLinha(linha)] ?? ""}
        pt={{
          wrapper: { className: styles.wrapper },
          // O Prime passa `context.index` ao bodyRow, mas a tipagem de `DataTableContext` não o declara.
          bodyRow: (opcoesDaLinha) => {
            const indice = (opcoesDaLinha?.context as { index?: number } | undefined)?.index ?? 0;
            const linha = linhas[indice];
            return {
              "data-testid": testIdLinha(indice),
              "data-save-failed": linha?.saveFailed ? "true" : undefined,
            };
          },
        }}
        data-testid="equipe-obra-table"
      >
        <Column
          header={EQUIPE_COLUMN_NOME}
          body={(linha: EquipeRow, { rowIndex }) => (
            <NomeCelula
              indice={rowIndex}
              linha={linha}
              opcoes={opcoes}
              profissionaisError={profissionaisError}
              disabled={disabled}
              conflitosDe={conflitosDaLinha(linha)}
              onChange={mudar(linha)}
            />
          )}
        />
        <Column
          header={EQUIPE_COLUMN_PAPEL}
          headerClassName={styles.colPapel}
          body={(linha: EquipeRow, { rowIndex }) => (
            <PapelCelula
              indice={rowIndex}
              linha={linha}
              papeis={papeis}
              disabled={disabled}
              onChange={mudar(linha)}
            />
          )}
        />
        <Column
          header={EQUIPE_COLUMN_INICIO}
          headerClassName={styles.colInicio}
          body={(linha: EquipeRow, { rowIndex }) => (
            <DataCelula
              indice={rowIndex}
              linha={linha}
              campo="inicio"
              disabled={disabled}
              onChange={mudar(linha)}
            />
          )}
        />
        <Column
          header={EQUIPE_COLUMN_FIM}
          headerClassName={styles.colFim}
          bodyClassName={styles.tdFim}
          body={(linha: EquipeRow, { rowIndex }) => (
            <DataCelula
              indice={rowIndex}
              linha={linha}
              campo="fim"
              disabled={disabled}
              onChange={mudar(linha)}
            />
          )}
        />
        <Column
          header={<span className={styles.somenteLeitor}>{EQUIPE_COLUMN_ACOES}</span>}
          headerClassName={styles.colAcoes}
          bodyClassName={styles.tdAcoes}
          body={(linha: EquipeRow, { rowIndex }) => (
            <AcoesCelula
              indice={rowIndex}
              linha={linha}
              nomeAcessivel={nomeDe(linha)}
              disabled={disabled}
              onConfirm={confirmar(linha)}
              onCancel={cancelar(linha)}
              onEdit={editar(linha)}
              onRemove={remover(linha)}
            />
          )}
        />
      </DataTable>
    </>
  );
}
